import { useEffect, useId, useState } from 'react';
import { Portal } from 'react-portal';

export default function NewsCardV2({
	imageUrl,
	title = '',
	description = '',
	linkText = 'Explore',
	onExplore,

	category,
	content, // html string
	badgeText = 'New',

	openOnCardClick = true, // click anywhere on card to open
}) {
	const [isOpen, setIsOpen] = useState(false);
	const dialogId = useId();

	const open = () => setIsOpen(true);
	const close = () => setIsOpen(false);

	// Body scroll lock + ESC close
	useEffect(() => {
		if (!isOpen) return;

		const prevOverflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';

		const onKeyDown = (e) => {
			if (e.key === 'Escape') close();
		};

		window.addEventListener('keydown', onKeyDown);
		return () => {
			document.body.style.overflow = prevOverflow;
			window.removeEventListener('keydown', onKeyDown);
		};
	}, [isOpen]);

	return (
		<>
			{/* MODAL */}
			{isOpen && (
				<Portal>
					<div
						className='fixed inset-0 z-[9999] flex items-center justify-center p-4 modal-news-new'
						aria-labelledby={`${dialogId}-title`}
						role='dialog'
						aria-modal='true'
					>
						{/* Backdrop */}
						<div className='absolute inset-0 bg-black/60 ' onClick={close} />

						{/* Panel */}
						<div className='relative z-10 w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-2xl '>
							{/* Close button */}
							<button
								type='button'
								onClick={close}
								aria-label='Close'
								className='absolute right-3 top-3 inline-flex h-10 w-10 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/75'
							>
								✕
							</button>

							{/* Image banner */}
							<div className='h-[240px] w-full overflow-hidden '>
								<img
									src={imageUrl}
									alt='news banner'
									className='h-full w-full object-cover'
								/>
							</div>

							{/* Content */}
							<div className='p-6 max-h-[300px] md:max-h-[500px] overflow-y-auto	'>
								{/* Badge */}
								<div className='mb-3 inline-flex items-center rounded-full border !border-green-aquinas bg-white px-3 py-1 text-xs font-semibold text-green-aquinas '>
									{badgeText}
								</div>

								{category ? (
									<div className='mb-2 text-xs font-medium text-slate-600 text-truncate text-uppercase '>
										{category}
									</div>
								) : null}

								<div
									id={`${dialogId}-title`}
									className='text-lg md:text-2xl font-semibold leading-tight text-slate-900 '
								>
									{title}
								</div>

								{/* Rich HTML */}
								<div
									className='prose prose-slate text-sm md:text-base mt-4 max-w-none '
									dangerouslySetInnerHTML={{ __html: content ?? '' }}
								/>
							</div>
						</div>
					</div>
				</Portal>
			)}

			{/* CARD */}
			<div
				role={openOnCardClick ? 'button' : undefined}
				tabIndex={openOnCardClick ? 0 : undefined}
				onClick={openOnCardClick ? open : undefined}
				onKeyDown={
					openOnCardClick
						? (e) => {
								if (e.key === 'Enter' || e.key === ' ') open();
							}
						: undefined
				}
				className='w-[400px] overflow-hidden rounded-[22px] group cursor-pointer select-none'
			>
				{/* Image */}
				<div className='h-[280px] w-full overflow-hidden'>
					<img
						src={imageUrl}
						alt='News images'
						className='h-full w-full rounded-xl object-cover transition-transform duration-300 ease-in-out group-hover:scale-110'
					/>
				</div>

				{/* Content */}
				<div className='pb-6 pt-2'>
					{/* Title */}
					<h2 className='mt-1 text-xl leading-[1.5rem] text-slate-900'>
						{title}
					</h2>

					{/* Description */}
					<p className='mt-1 text-[13px] font-normal leading-6 text-slate-600 line-clamp-2'>
						{description}
					</p>

					{/* Link */}
					<button
						type='button'
						onClick={(e) => {
							// prevent card click double-trigger
							e.stopPropagation();
							open();
							onExplore?.();
						}}
						className='mt-2 inline-flex text-xs font-medium text-slate-600 underline underline-offset-4 hover:text-green-aquinas'
					>
						{linkText}
					</button>
				</div>
			</div>

			<style jsx global>{`
				.modal-news-new,
				.modal-news-new * {
					animation: none !important;
					animation-name: none !important;
					animation-duration: 0s !important;
				}
			`}</style>
		</>
	);
}
